/**
 * SharePlaylistModal Component
 * Modal for sharing a playlist with friends
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getFriends,
  getPlaylistShares,
  removePlaylistShare,
  sharePlaylist,
  updatePlaylistSharePermission,
  type Friend,
  type PlaylistShare,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import ModalPortal from './ModalPortal';
import UsernameSetupModal from './UsernameSetupModal';

interface Props {
  isOpen: boolean;
  playlistId: number;
  onClose: () => void;
}

export default function SharePlaylistModal({ isOpen, playlistId, onClose }: Props) {
  const { hasUsername } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [shares, setShares] = useState<PlaylistShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [friendsData, sharesData] = await Promise.all([
        getFriends(),
        getPlaylistShares(playlistId),
      ]);
      setFriends(friendsData);
      setShares(sharesData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [playlistId]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      loadData();
    }
  }, [isOpen, loadData]);

  if (!isOpen) return null;

  const sharedUserIds = new Set(shares.map((s) => s.user.id));
  const unsharedFriends = friends.filter((f) => !sharedUserIds.has(f.id));

  const handleShare = async (friendId: number, permission: 'read' | 'edit') => {
    if (!hasUsername) {
      setShowUsernameModal(true);
      return;
    }

    setActionError(null);
    try {
      await sharePlaylist(playlistId, friendId, permission);
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to share playlist');
    }
  };

  const handleUpdatePermission = async (userId: number, permission: 'read' | 'edit') => {
    setActionError(null);
    try {
      await updatePlaylistSharePermission(playlistId, userId, permission);
      setShares((prev) => prev.map((s) => (s.user.id === userId ? { ...s, permission } : s)));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update permission');
    }
  };

  const handleRemoveShare = async (userId: number) => {
    setActionError(null);
    try {
      await removePlaylistShare(playlistId, userId);
      setShares((prev) => prev.filter((s) => s.user.id !== userId));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove share');
    }
  };

  const handleClose = () => {
    setError(null);
    setActionError(null);
    onClose();
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Share Playlist</h2>
            <button className="modal-close" onClick={handleClose}>
              &times;
            </button>
          </div>

          {!hasUsername && (
            <div className="username-prompt">
              <p>You need a username to share playlists.</p>
              <button onClick={() => setShowUsernameModal(true)}>Set Username</button>
            </div>
          )}

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              {actionError && <div className="error-message">{actionError}</div>}

              {/* Current Shares */}
              {shares.length > 0 && (
                <section className="share-section">
                  <h3>Shared With</h3>
                  <ul className="share-list">
                    {shares.map((share) => (
                      <li key={share.user.id} className="share-item">
                        <div className="user-info">
                          {share.user.picture ? (
                            <img src={share.user.picture} alt="" className="user-avatar small" />
                          ) : (
                            <div className="user-avatar small placeholder">
                              {share.user.username?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div className="user-details">
                            <span className="user-username">@{share.user.username}</span>
                            {share.user.name && (
                              <span className="user-name">{share.user.name}</span>
                            )}
                          </div>
                        </div>
                        <div className="share-controls">
                          <select
                            value={share.permission}
                            onChange={(e) =>
                              handleUpdatePermission(
                                share.user.id,
                                e.target.value as 'read' | 'edit'
                              )
                            }
                          >
                            <option value="read">View only</option>
                            <option value="edit">Can edit</option>
                          </select>
                          <button
                            className="small secondary danger"
                            onClick={() => handleRemoveShare(share.user.id)}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Add New Share */}
              {unsharedFriends.length > 0 && (
                <section className="share-section">
                  <h3>Add Friends</h3>
                  <ul className="share-list">
                    {unsharedFriends.map((friend) => (
                      <li key={friend.id} className="share-item">
                        <div className="user-info">
                          {friend.picture ? (
                            <img src={friend.picture} alt="" className="user-avatar small" />
                          ) : (
                            <div className="user-avatar small placeholder">
                              {friend.username?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div className="user-details">
                            <span className="user-username">@{friend.username}</span>
                            {friend.name && <span className="user-name">{friend.name}</span>}
                          </div>
                        </div>
                        <div className="share-controls">
                          <button
                            className="small"
                            onClick={() => handleShare(friend.id, 'read')}
                            disabled={!hasUsername}
                          >
                            Share (View)
                          </button>
                          <button
                            className="small secondary"
                            onClick={() => handleShare(friend.id, 'edit')}
                            disabled={!hasUsername}
                          >
                            Share (Edit)
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {friends.length === 0 && (
                <div className="empty-state">
                  <p>You don't have any friends yet.</p>
                  <a href="/friends">Add friends to share playlists</a>
                </div>
              )}

              {friends.length > 0 && unsharedFriends.length === 0 && shares.length > 0 && (
                <p className="all-shared">This playlist is shared with all your friends.</p>
              )}
            </>
          )}

          <div className="modal-actions">
            <button className="secondary" onClick={handleClose}>
              Done
            </button>
          </div>

          <UsernameSetupModal
            isOpen={showUsernameModal}
            onClose={() => setShowUsernameModal(false)}
            onSuccess={() => setShowUsernameModal(false)}
          />
        </div>
      </div>
    </ModalPortal>
  );
}
